import Select, {
    components,
    DropdownIndicatorProps,
    OptionProps,
    SingleValueProps
} from "react-select";
import { ReactComponent as ChevronDown } from "../../../resources/icons/chevron-down.svg";
import { ReactComponent as CheckboxIcon } from "../../../resources/icons/checkbox.svg";
import { ReactComponent as CheckboxIconSelected } from "../../../resources/icons/checkbox_selected.svg";

export interface Props {
    inputId: string;
    placeholder: string;
    options: { value: string; label: string }[];
    selectedOptions: string[];
    onOptionClicked: (option: { value: string; label: string }) => void;
}

export function FstyrMultiSelect({
    selectedOptions,
    options,
    onOptionClicked,
    placeholder,
    inputId
}: Props) {
    const label =
        selectedOptions.length === 1
            ? options.find((it) => it.value === selectedOptions[0])?.label
            : selectedOptions.length + " valgte";

    const DropdownIndicator: React.FC<DropdownIndicatorProps> = (props) => {
        const {
            selectProps: { menuIsOpen }
        } = props;
        return (
            <components.DropdownIndicator {...props}>
                <ChevronDown
                    className={`transition-transform ${
                        menuIsOpen ? "rotate-180" : "rotate-0"
                    }`}
                />
            </components.DropdownIndicator>
        );
    };

    const Option: React.FC<OptionProps> = (props) => {
        const label = props.label;
        const data = props.data as { value: string };
        const isSelected = selectedOptions.includes(data.value);
        return (
            <components.Option {...props}>
                <span className="flex items-center gap-8">
                    <span>
                        {isSelected ? (
                            <CheckboxIconSelected />
                        ) : (
                            <CheckboxIcon />
                        )}
                    </span>
                    {label}
                </span>
            </components.Option>
        );
    };

    const SingleValue: React.FC<SingleValueProps> = (props) => {
        return (
            <components.SingleValue {...props}>{label}</components.SingleValue>
        );
    };

    return (
        <Select
            inputId={inputId}
            styles={{
                control: (baseStyles, state) => ({
                    ...baseStyles,
                    borderColor: state.isFocused
                        ? "var(--color-primary-identity-blue)"
                        : "var(--color-secondary-blue-light)",
                    fontFamily: "IBM Plex Sans; sans-serif",
                    borderRadius: "12px",
                    backgroundColor: "var(--color-system-white)",
                    "&:hover": {
                        borderColor: "var(--color-primary-identity-blue)"
                    },
                    boxShadow: "none",
                    paddingLeft: "12px",
                    paddingRight: "12px",
                    height: "56px",
                    fontSize: "16px"
                }),
                menu: (baseStyles, state) => ({
                    ...baseStyles,
                    paddingTop: "5px",
                    paddingBottom: "5px",
                    paddingLeft: "12px",
                    paddingRight: "12px",
                    borderRadius: "12px",
                    borderColor: "var(--color-primary-identity-blue)"
                }),
                placeholder: (baseStyles, state) => ({
                    ...baseStyles,
                    color: "var(--color-system-placeholder-text)"
                }),
                option: (baseStyles, state) => ({
                    ...baseStyles,
                    backgroundColor: "var(--color-system-white)",
                    paddingRight: "20px",
                    borderRadius: "12px",
                    "&:hover": {
                        borderRadius: "12px",
                        backgroundColor: "var(--color-secondary-blue-lightest)"
                    },
                    color: "var(--color-secondary-gray-dark)",
                    fontFamily: "IBM Plex Sans; sans-serif",
                    fontSize: "16px"
                }),
                indicatorSeparator: (baseStyles, state) => ({
                    ...baseStyles,
                    display: "none"
                })
            }}
            value={selectedOptions.length > 0 ? selectedOptions : null}
            components={{ DropdownIndicator, Option, SingleValue }}
            placeholder={placeholder}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            onChange={(newValue) => {
                onOptionClicked(newValue as { value: string; label: string });
            }}
            options={options}
        />
    );
}
